// utils/visited-pages.ts

export const STORAGE_KEY = 'nesa_visited_pages'
export const MAX_RAW_ENTRIES = 50
export const MAX_DISPLAY_ENTRIES = 10

// System URLs to SKIP at write time. Extend as needed.
export const SKIP_URL_PREFIXES = [
	'/api/',
	'/_next/',
	'/_error',
	'/page-api/',
	'/search',
	'/search-syllabus',
	'/find-syllabus',
	'/404',
	'/error',
]

// Known syllabus tab segments (mirror of SYLLABUS_TABS - kept local to avoid import cycles)
export const SYLLABUS_TAB_SEGMENTS = [
	'overview',
	'rationale',
	'aim',
	'outcomes',
	'content',
	'assessment',
	'glossary',
	'teaching-and-learning',
]

export interface VisitedPage {
	url: string // RAW url as user visited it (may include query params, deeper syllabus paths)
	title: string // RAW document.title at visit time
	visitedAt: number
}

export interface DisplayablePage {
	url: string // normalized
	title: string // cleaned
}

/** Strips query string + hash from a URL path. */
export const stripQuery = (url: string): string => {
	const qIdx = url.indexOf('?')
	const hIdx = url.indexOf('#')
	const cutAt = [qIdx, hIdx].filter((i) => i >= 0).sort((a, b) => a - b)[0]
	return cutAt === undefined ? url : url.slice(0, cutAt)
}

// URLs to always exclude (exact match — can't use startsWith for '/')
const SKIP_URL_EXACT = ['/']

/** True when URL should be skipped at WRITE time (system pages). */
export const isExcludedFromWrite = (url: string): boolean => {
	const path = stripQuery(url)
	if (SKIP_URL_EXACT.includes(path)) return true
	return SKIP_URL_PREFIXES.some((prefix) => path.startsWith(prefix))
}

/** True when URL should be excluded at READ time (non-content pages). */
export const isExcludedFromDisplay = (url: string): boolean => {
	const path = stripQuery(url)
	return SKIP_URL_EXACT.includes(path)
}

/** True if URL is a syllabus page DEEPER than the tab level. */
export const isSyllabusTabDeepPath = (url: string): boolean => {
	const path = stripQuery(url)
	const segments = path.split('/').filter(Boolean)
	// Pattern: learning-areas / [learningarea] / [syllabus] / [tab] / [...deeper]
	if (segments[0] !== 'learning-areas') return false
	if (segments.length <= 4) return false
	const tab = segments[3]
	return SYLLABUS_TAB_SEGMENTS.includes(tab)
}

/** Normalize a raw URL for display/dedup. Strips query params. */
export const normalizeUrl = (url: string): string => {
	return stripQuery(url)
}

/** Trim deep syllabus URLs to tab level. Non-syllabus URLs pass through unchanged. */
export const normalizeSyllabusUrl = (url: string): string => {
	const segments = url.split('/').filter(Boolean)
	if (segments[0] !== 'learning-areas') return url
	if (segments.length <= 4) return url
	if (!SYLLABUS_TAB_SEGMENTS.includes(segments[3])) return url
	return '/' + segments.slice(0, 4).join('/')
}

const SYLLABUS_TAB_DISPLAY_NAMES: Record<string, string> = {
	overview: '',
	rationale: 'Rationale',
	aim: 'Aim',
	outcomes: 'Outcomes',
	content: 'Content',
	assessment: 'Assessment',
	glossary: 'Glossary',
	'teaching-and-learning': 'Teaching and learning',
}

/** Clean a raw document.title for display. */
export const cleanTitle = (
	rawTitle: string,
	normalizedUrl: string,
	wasDeepSyllabus: boolean,
): string => {
	const firstPart = rawTitle
		.split(' | ')[0]
		.replace(/\s*\|+\s*$/, '')
		.trim()
	// HARDCODED SPECIAL CASE: overview tab — strip " - Course overview" suffix
	if (normalizedUrl.endsWith('/overview')) {
		return firstPart.replace(/\s*-\s*Course overview\s*$/i, '').trim()
	}
	// Deep syllabus page normalized to tab level — derive tab-level title.
	// Deep page titles follow pattern: "[Syllabus Name] - [Section] - [Focus Area]"
	// where section can be "Stage X", "Year X", "Life Skills", etc.
	// Split on the first " - " to extract the syllabus name.
	if (wasDeepSyllabus) {
		const segments = normalizedUrl.split('/').filter(Boolean)
		const tabSegment = segments[3]
		const syllabusName = firstPart.split(' - ')[0].trim()
		const tabName = SYLLABUS_TAB_DISPLAY_NAMES[tabSegment]
		if (tabName) return `${syllabusName} - ${tabName}`
		return syllabusName
	}
	return firstPart
}

/**
 * Given raw entries + the current page URL, produce the displayable list:
 *   - filter deep syllabus pages
 *   - normalize URLs
 *   - clean titles
 *   - dedupe by normalized URL (keep most recent)
 *   - exclude current page
 *   - sort desc by visitedAt
 *   - take top MAX_DISPLAY_ENTRIES
 */
export const buildDisplayList = (
	raw: VisitedPage[],
	currentPath: string,
): DisplayablePage[] => {
	const currentNormalized = normalizeSyllabusUrl(normalizeUrl(currentPath))
	const byNormalized = new Map<string, VisitedPage>()

	for (const entry of raw) {
		const normalized = normalizeSyllabusUrl(normalizeUrl(entry.url))
		if (normalized === currentNormalized) continue
		if (isExcludedFromDisplay(normalized)) continue
		const existing = byNormalized.get(normalized)
		if (!existing || entry.visitedAt > existing.visitedAt) {
			byNormalized.set(normalized, entry)
		}
	}

	return Array.from(byNormalized.values())
		.sort((a, b) => b.visitedAt - a.visitedAt)
		.slice(0, MAX_DISPLAY_ENTRIES)
		.map((entry) => {
			const rawUrl = normalizeUrl(entry.url)
			const url = normalizeSyllabusUrl(rawUrl)
			const wasDeepSyllabus = url !== rawUrl
			return {
				url,
				title: cleanTitle(entry.title, url, wasDeepSyllabus),
			}
		})
		.filter((entry) => entry.title.trim().length > 0)
}
